/**
 * Payment terms.
 *
 * The list Sales chooses between. Held as data so a new approved option is an
 * administrator's edit, not a release.
 */
import { useEffect, useState } from 'react';
import { endpoints, type PaymentTerm } from '../../lib/api';
import { Alert, Button, Card, Field, Input, Skeleton } from '../../components/ui';
import { AdminTable, Empty, StatusPill } from './shared';

export function PaymentTermsAdmin({ onError }: { onError(message: string): void }) {
  const [terms, setTerms] = useState<PaymentTerm[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    endpoints
      .adminConfiguration()
      .then((result) => setTerms(result.paymentTerms))
      .catch((caught) => onError(String(caught)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (id: string, action: () => Promise<unknown>, message: string) => {
    setBusyId(id);
    setNotice(null);
    try {
      await action();
      await load();
      setNotice(message);
      setCreating(false);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusyId(null);
    }
  };

  if (!terms) return <Skeleton className="h-56 w-full" />;

  return (
    <Card
      title="Payment terms"
      description="The options Sales may choose between, and the wording each renders into the proposal."
      actions={
        <Button size="sm" onClick={() => setCreating(true)} disabled={creating}>
          Add term
        </Button>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {creating && (
        <TermForm
          busy={busyId === 'new'}
          nextSortOrder={terms.length}
          onCancel={() => setCreating(false)}
          onSubmit={(values) =>
            void run(
              'new',
              () => endpoints.createPaymentTerm(values),
              `"${values.displayName}" added.`,
            )
          }
        />
      )}

      {terms.length === 0 ? (
        <Empty>No payment terms configured.</Empty>
      ) : (
        <AdminTable
          minWidth={860}
          headers={['Order', 'Display name', 'Template text', 'Status', 'Default', 'Actions']}
        >
          {terms.map((term) => (
            <tr key={term.paymentTermId} className="align-top">
              <td className="px-5 py-3 tabular-nums text-muted">{term.sortOrder}</td>
              <td className="px-5 py-3">
                <span className="font-medium text-ink">{term.displayName}</span>
                <span className="block text-[11px] text-muted">{term.paymentTermId}</span>
              </td>
              <td className="px-5 py-3 text-body">
                {term.allowsCustomText ? (
                  <span className="text-muted">Sales types the wording</span>
                ) : (
                  term.templateText
                )}
              </td>
              <td className="px-5 py-3">
                <StatusPill status={term.status} />
              </td>
              <td className="px-5 py-3">
                {term.isDefault ? (
                  <span className="text-[12px] font-semibold text-teal">Default</span>
                ) : term.allowsCustomText ? (
                  <span className="text-[12px] text-muted">—</span>
                ) : (
                  <button
                    type="button"
                    disabled={busyId === term.paymentTermId || term.status !== 'ACTIVE'}
                    onClick={() =>
                      void run(
                        term.paymentTermId,
                        () =>
                          endpoints.updatePaymentTerm(term.paymentTermId, { isDefault: true }),
                        `"${term.displayName}" is now the default.`,
                      )
                    }
                    className="text-[12px] font-semibold text-teal underline disabled:opacity-40"
                  >
                    Make default
                  </button>
                )}
              </td>
              <td className="px-5 py-3">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={busyId === term.paymentTermId}
                    onClick={() =>
                      void run(
                        term.paymentTermId,
                        () =>
                          endpoints.updatePaymentTerm(term.paymentTermId, {
                            status: term.status === 'ACTIVE' ? 'RETIRED' : 'ACTIVE',
                          }),
                        term.status === 'ACTIVE'
                          ? `"${term.displayName}" retired.`
                          : `"${term.displayName}" restored.`,
                      )
                    }
                    className="text-[12px] font-semibold text-muted underline disabled:opacity-50"
                  >
                    {term.status === 'ACTIVE' ? 'Retire' : 'Restore'}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === term.paymentTermId}
                    onClick={() =>
                      void run(
                        term.paymentTermId,
                        () => endpoints.deletePaymentTerm(term.paymentTermId),
                        `"${term.displayName}" deleted.`,
                      )
                    }
                    className="text-[12px] font-semibold text-muted underline disabled:opacity-50"
                    title="Only for one added by mistake. Retiring keeps proposals explainable."
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </AdminTable>
      )}

      <p className="mt-4 text-[12px] text-muted">
        Retiring is the normal way to withdraw an option: a proposal that already chose a term
        stores its wording, so the choice stays explainable. Delete is only for a term added by
        mistake. At least one selectable term always has to remain.
      </p>
    </Card>
  );
}

function TermForm({
  busy,
  nextSortOrder,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  nextSortOrder: number;
  onCancel(): void;
  onSubmit(values: {
    displayName: string;
    templateText: string;
    sortOrder: number;
    isDefault: boolean;
  }): void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [sortOrder, setSortOrder] = useState(nextSortOrder);
  const [isDefault, setIsDefault] = useState(false);

  return (
    <form
      className="mb-5 space-y-3 rounded-md bg-tint px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ displayName, templateText, sortOrder, isDefault });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Display name" htmlFor="term-name" required hint="What Sales sees.">
          <Input
            id="term-name"
            value={displayName}
            required
            maxLength={80}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field
          label="Template text"
          htmlFor="term-text"
          required
          hint="Rendered verbatim into the proposal."
        >
          <Input
            id="term-text"
            value={templateText}
            required
            maxLength={300}
            onChange={(event) => setTemplateText(event.target.value)}
          />
        </Field>
        <Field label="Sort order" htmlFor="term-order">
          <Input
            id="term-order"
            type="number"
            min={0}
            max={999}
            value={sortOrder}
            onChange={(event) => setSortOrder(Number(event.target.value))}
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-[13px] text-body">
        <input
          type="checkbox"
          className="size-4 accent-teal"
          checked={isDefault}
          onChange={(event) => setIsDefault(event.target.checked)}
        />
        Make this the default for new proposals
      </label>

      <div className="flex gap-3">
        <Button type="submit" size="sm" loading={busy}>
          Add term
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
